import { HTMLAttributes, useEffect, useRef } from 'react'
import { Transition } from 'react-transition-group'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

import SVGIcon from '@/components/base/svg-icon'
import { useTransitionStore } from '@/contexts/transition/use-transition-store'
import { useLoaderStore } from '@/store/loader'

import Spinner from './spinner'
import { cn } from '@/lib/utils'

const LOADER_TIMEOUT = 3000

export type LoaderProps = HTMLAttributes<HTMLDivElement>

const Loader = ({ className, ...props }: LoaderProps) => {
  const root = useRef<HTMLDivElement>(null)
  const { contextSafe } = useGSAP({ scope: root })
  const loaded = useLoaderStore((state) => state.loaded)
  const setLoaderState = useLoaderStore((state) => state.setState)
  const setTransitionState = useTransitionStore((state) => state.setState)

  useEffect(() => {
    if (loaded) return

    const timeout = setTimeout(() => {
      setLoaderState({ pageLoaded: true, loaded: true })
    }, LOADER_TIMEOUT)

    return () => clearTimeout(timeout)
  }, [loaded, setLoaderState])

  const createTimeline = contextSafe(() =>
    gsap
      .timeline({ defaults: { duration: 1, ease: 'power2.inOut', transformOrigin: 'top' } })
      .to(
        ['[data-motion-layer-2]', '[data-motion-layer-1]', '[data-motion-layer-0]'],
        { scaleY: 0, stagger: { amount: 0.3 } },
        0
      )
      .to('[data-motion-layer-3]', { yPercent: -100 }, 0.3)
      .to('[data-motion-layer-4]', { yPercent: 100 }, '<')
  )

  const handleEndListener = contextSafe(async (done: () => void) => {
    setTransitionState({ active: false })
    gsap.delayedCall(0.2, () => setTransitionState({ landing: true }))
    await createTimeline()
    setTransitionState({ active: false, landing: false })
    setLoaderState({ loading: false })
    done()
  })

  return (
    <Transition nodeRef={root} in={!loaded} addEndListener={handleEndListener} unmountOnExit>
      <div ref={root} className={cn('fixed left-0 top-0 z-loader size-full', className)} {...props}>
        <div className="absolute left-0 top-0 size-full bg-dark" data-motion-layer-0 />
        <div className="absolute left-0 top-0 size-full bg-black/50" data-motion-layer-1 />
        <div className="absolute left-0 top-0 size-full bg-black" data-motion-layer-2 />
        <div className="absolute left-0 top-0 flex size-full overflow-hidden" data-motion-layer-3>
          <div
            className="absolute left-0 top-0 flex size-full flex-col items-center justify-center text-white"
            data-motion-layer-4
          >
            <SVGIcon symbol="logo-full" className="h-[4.8rem] w-full max-w-[24.5rem]" />

            <div className="absolute bottom-[5rem] left-1/2 -translate-x-1/2 md:bottom-[6rem]">
              <Spinner variant="medium" />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  )
}

export default Loader
